import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../presentation/hooks/store-hooks';
import { useDossieRepository, IConsultaOnlineRequest } from '../../infra/dossie';
import { setIsLoading } from '../../application/states/loading';
import {
  setDossieResult,
  setDossieError,
  setComplianceAlert,
  clearComplianceAlert,
  setCobrancaAlert,
  clearCobrancaAlert,
  setOrigemConsulta,
  setOrigemMinimizada,
  clearOrigemConsulta,
  setModoVisualizacaoCadastral,
  updateSituacaoCadastral,
  IOrigemConsulta,
  IModoVisualizacaoCadastral,
} from '../../application/states/dossie';
import { useToastAlertService } from '../toast-alert';
import type { IDossiePFResponse, IDossiePJResponse, IConsultarDossieOptions } from '../../application/models/dossie';

import { isPessoaFisica, cleanDocument, formatDocument } from '../../presentation/pages/private-section/dossie-results/components/utils/formatters';

export type { IConsultarDossieOptions, IOrigemConsulta };

export const useDossieService = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    consultaDossiePF,
    consultaDossiePJ,
    registrarOptinAcordo: repoRegistrarOptinAcordo,
    consultarReceitaOnlinePF: repoConsultarReceitaOnlinePF,
    consultarReceitaOnlinePJ: repoConsultarReceitaOnlinePJ,
  } = useDossieRepository();
  const { showError, showSuccess } = useToastAlertService();
  const isLoading = useAppSelector((state) => state.loading.isLoading);
  const error = useAppSelector((state) => state.dossie.error);
  const currentResult = useAppSelector((state) => state.dossie.currentResult);
  const complianceAlert = useAppSelector((state) => state.dossie.complianceAlert);
  const cobrancaAlert = useAppSelector((state) => state.dossie.cobrancaAlert);
  const origemConsulta = useAppSelector((state) => state.dossie.origemConsulta);
  const origemMinimizada = useAppSelector((state) => state.dossie.origemMinimizada);
  const modoVisualizacaoCadastral = useAppSelector((state) => state.dossie.modoVisualizacaoCadastral);
  const situacaoCadastralOriginal = useAppSelector((state) => state.dossie.situacaoCadastralOriginal);
  const situacaoCadastralOnline = useAppSelector((state) => state.dossie.situacaoCadastralOnline);

  const alternarVisualizacaoCadastral = useCallback((modo: IModoVisualizacaoCadastral) => {
    dispatch(setModoVisualizacaoCadastral(modo));
  }, [dispatch]);

  const situacaoCadastralAtiva = useMemo(() => {
    if (situacaoCadastralOnline) {
      return situacaoCadastralOnline;
    }
    return situacaoCadastralOriginal;
  }, [situacaoCadastralOnline, situacaoCadastralOriginal]);

  const consultarDossie = useCallback((documento?: string, options?: IConsultarDossieOptions) => {
    if (!documento) return;

    // Se for Menor de Idade ou Pessoa Restrita, NÃO realiza a busca e abre o alerta de conformidade
    if (options?.menorIdade || options?.pessoaRestrita) {
      dispatch(setComplianceAlert({
        open: true,
        menorIdade: options.menorIdade,
        pessoaRestrita: options.pessoaRestrita,
        nome: options.nome
      }));
      return;
    }

    const cleanDoc = documento.replace(/[^A-Za-z0-9]/g, '');
    if (cleanDoc) {
      navigate(`/private/dossie/results/${cleanDoc}`);
    }
  }, [dispatch, navigate]);

  const fecharComplianceAlert = useCallback(() => {
    dispatch(clearComplianceAlert());
  }, [dispatch]);

  const registrarOptinAcordo = useCallback(async (idVeles: number, deAcordo: boolean) => {
    try {
      await repoRegistrarOptinAcordo({ idVeles, deAcordo });
    } catch (err: any) {
      showError(err.response.data.message);
      throw err;
    }
  }, [repoRegistrarOptinAcordo, showError]);

  const solicitarConsultaComCobranca = useCallback((
    documento: string,
    idVeles: number,
    documentoOrigem: string,
    options?: IConsultarDossieOptions,
    origemInfo?: { nome?: string }
  ) => {
    const isPFOrigem = isPessoaFisica(documentoOrigem);
    const nomeOrigem =
      origemInfo?.nome ||
      (isPFOrigem
        ? (currentResult as any)?.localizacao?.dadosPessoa?.Nome
        : (currentResult as any)?.localizacao?.dadosPessoa?.RazaoSocial || (currentResult as any)?.localizacao?.dadosPessoa?.NomeFantasia);

    const origem: IOrigemConsulta = {
      documento: documentoOrigem,
      nome: nomeOrigem,
      tipo: isPFOrigem ? 'CPF' : 'CNPJ',
    };

    if (options?.menorIdade || options?.pessoaRestrita) {
      consultarDossie(documento, options);
      return;
    }

    const cruzada = isPFOrigem !== isPessoaFisica(documento);
    if (!cruzada) {
      dispatch(setOrigemConsulta(origem));
      consultarDossie(documento, options);
      return;
    }

    dispatch(setCobrancaAlert({
      open: true,
      documento,
      idVeles,
      nome: options?.nome,
      options,
      origemConsulta: origem,
    }));
  }, [consultarDossie, currentResult, dispatch]);

  const fecharCobrancaAlert = useCallback(async () => {
    if (!cobrancaAlert) return;
    const { idVeles } = cobrancaAlert;
    dispatch(clearCobrancaAlert());

    if (idVeles !== undefined && idVeles !== null && idVeles > 0) {
      try {
        await repoRegistrarOptinAcordo({ idVeles, deAcordo: false });
      } catch (err: any) {
        console.error('Erro ao registrar recusa de opt-in:', err);
      }
    }
  }, [cobrancaAlert, dispatch, repoRegistrarOptinAcordo]);

  const confirmarCobrancaAlert = useCallback(async () => {
    if (!cobrancaAlert) return;
    const { documento, idVeles, options, origemConsulta: alertOrigem } = cobrancaAlert;
    dispatch(setIsLoading({ isLoading: true }));
    try {
      if (idVeles !== undefined && idVeles !== null && idVeles > 0) {
        await registrarOptinAcordo(idVeles, true);
      }
      if (alertOrigem) {
        dispatch(setOrigemConsulta(alertOrigem));
      }
      dispatch(clearCobrancaAlert());
      consultarDossie(documento, options);
    } finally {
      dispatch(setIsLoading({ isLoading: false }));
    }
  }, [cobrancaAlert, dispatch, registrarOptinAcordo, consultarDossie]);

  const fecharOrigemConsulta = useCallback(() => {
    dispatch(clearOrigemConsulta());
  }, [dispatch]);

  const toggleOrigemMinimizada = useCallback(() => {
    dispatch(setOrigemMinimizada(!origemMinimizada));
  }, [dispatch, origemMinimizada]);

  const voltarParaOrigem = useCallback(() => {
    if (!origemConsulta) return;
    const docOrigem = origemConsulta.documento;
    const cleanDocOrigem = cleanDocument(docOrigem);

    // Prepara a nova origem que será salva quando a consulta de volta for confirmada
    let novaOrigem: IOrigemConsulta | null = null;
    if (currentResult) {
      const rawDocAtual = (currentResult as any)?.localizacao?.dadosPessoa?.Documento || '';
      const nomeAtual =
        (currentResult as any)?.localizacao?.dadosPessoa?.Nome ||
        (currentResult as any)?.localizacao?.dadosPessoa?.RazaoSocial ||
        (currentResult as any)?.localizacao?.dadosPessoa?.NomeFantasia;

      if (rawDocAtual) {
        novaOrigem = {
          documento: rawDocAtual,
          nome: nomeAtual,
          tipo: isPessoaFisica(rawDocAtual) ? 'CPF' : 'CNPJ',
        };
      }
    }

    // Busca o IdVeles correspondente no dossiê atual, caso disponível
    let idVelesAlvo = origemConsulta.idVeles || 0;
    if (!idVelesAlvo && currentResult) {
      // Se estamos em PJ, busca o sócio correspondente pelo DocumentoSocio
      const socios = (currentResult as any)?.socios?.socios;
      if (Array.isArray(socios)) {
        const socio = socios.find((s: any) => cleanDocument(s.DocumentoSocio) === cleanDocOrigem);
        if (socio?.IdVelesSocio) {
          idVelesAlvo = socio.IdVelesSocio;
        }
      }

      // Se estamos em PF, busca a empresa correspondente pelo Documento
      const participacoes = (currentResult as any)?.participacaoEmpresarial;
      if (Array.isArray(participacoes)) {
        const empresa = participacoes.find((p: any) => cleanDocument(p.Documento) === cleanDocOrigem);
        if (empresa?.IdVeles) {
          idVelesAlvo = empresa.IdVeles;
        }
      }
    }

    // Dispara o modal de confirmação de nova consulta/cobrança
    dispatch(
      setCobrancaAlert({
        open: true,
        documento: docOrigem,
        idVeles: idVelesAlvo,
        nome: origemConsulta.nome,
        options: {
          nome: origemConsulta.nome!,
          menorIdade: false,
          pessoaRestrita: false
        },
        origemConsulta: novaOrigem,
      })
    );
  }, [origemConsulta, currentResult, dispatch]);

  const fetchDossie = useCallback(async (documento: string): Promise<IDossiePFResponse | IDossiePJResponse> => {
    dispatch(setIsLoading({ isLoading: true }));
    dispatch(setDossieError(null));

    // Remove any formatting to get the raw document string
    const rawDocumento = documento.replace(/[^A-Za-z0-9]/g, '');
    const isAlphanumeric = /[A-Z]/i.test(rawDocumento);
    const isPF = !isAlphanumeric && rawDocumento.length <= 11;

    try {
      const response = isPF
        ? await consultaDossiePF({ documento: rawDocumento })
        : await consultaDossiePJ({ documento: rawDocumento });

      // The backend returns the data directly or wrapped in 'result'
      const result: IDossiePFResponse | IDossiePJResponse = response.data?.result || response.data;

      if (result?.localizacao?.telefones && Array.isArray(result.localizacao.telefones)) {
        result.localizacao.telefones.forEach((t) => {
          if (t.WhatsApp || String(t.WhatsApp).toLowerCase() === 'true' || String(t.WhatsApp) === '1') {
            t.Premium = true;
          }
        });
      }

      dispatch(setDossieResult(result));
      return result;
    } catch (err: any) {
      let errorMessage = err?.response?.data?.message || err?.message || 'Erro ao buscar dossiê. Tente novamente.';

      if (errorMessage.includes('Menor de Idade') || errorMessage.includes('Pessoa Restrita')) {
        const isMinor = errorMessage.includes('Menor de Idade');
        const isRestricted = errorMessage.includes('Pessoa Restrita');
        dispatch(setComplianceAlert({
          open: true,
          menorIdade: isMinor,
          pessoaRestrita: isRestricted,
          nome: rawDocumento
        }));
        dispatch(setDossieResult(null));
        return null as any;
      }

      if (errorMessage.toLowerCase().includes('jwt')) {
        errorMessage = 'Sessão inválida ou expirada. Por favor, acesse o Dossiê através do Portal Veles novamente.';
      }

      dispatch(setDossieError(errorMessage));
      showError(errorMessage);
      throw err;
    } finally {
      dispatch(setIsLoading({ isLoading: false }));
    }
  }, [dispatch, consultaDossiePF, consultaDossiePJ, showError]);

  const consultarReceitaOnline = useCallback(
    async (params: IConsultaOnlineRequest) => {
      const isPF = isPessoaFisica(params.documento);
      const rawDocumento = formatDocument(params.documento, isPF);

      try {
        const response = isPF
          ? await repoConsultarReceitaOnlinePF({
              documento: rawDocumento,
              dataNascimento: params.dataNascimento,
            })
          : await repoConsultarReceitaOnlinePJ({
              documento: rawDocumento,
              dataAbertura: params.dataAbertura,
            });

        const data = response.data;
        if (data?.resultado?.situacaoCadastral) {
          const sc = data.resultado.situacaoCadastral;
          const payload = !isPF && !sc.situacaoCadastral ? { situacaoCadastral: sc } : sc;
          dispatch(updateSituacaoCadastral(payload));
          showSuccess('Situação Cadastral atualizada com sucesso na Receita Federal!');
        }

        return data;
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.message || err?.message || 'Erro ao realizar consulta online na Receita Federal.';
        showError(errorMessage);
        throw err;
      }
    },
    [dispatch, repoConsultarReceitaOnlinePF, repoConsultarReceitaOnlinePJ, showError, showSuccess]
  );

  return {
    fetchDossie,
    consultarDossie,
    consultarReceitaOnline,
    complianceAlert,
    fecharComplianceAlert,
    cobrancaAlert,
    solicitarConsultaComCobranca,
    fecharCobrancaAlert,
    confirmarCobrancaAlert,
    registrarOptinAcordo,
    origemConsulta,
    origemMinimizada,
    fecharOrigemConsulta,
    clearOrigemConsulta: fecharOrigemConsulta,
    toggleOrigemMinimizada,
    voltarParaOrigem,
    loading: isLoading,
    error,
    currentResult,
    modoVisualizacaoCadastral,
    situacaoCadastralOnline,
    situacaoCadastralOriginal,
    situacaoCadastralAtiva,
    alternarVisualizacaoCadastral
  };
};
